import { isTestMode } from './test-mode'
import { getAgentUrl } from '../services/agent-url'

interface User {
  id: string
  email?: string
}

interface Session {
  access_token: string
  user: User
}

type AuthStateCallback = (event: string, session: Session | null) => void

export interface AuthClient {
  getSession(): Promise<{ data: { session: Session | null } }>
  signInWithPassword(params: { email: string; password: string }): Promise<{ error: { message: string } | null }>
  signUp(params: { email: string; password: string }): Promise<{ error: { message: string } | null }>
  signOut(): Promise<void>
  onAuthStateChange(callback: AuthStateCallback): { data: { subscription: { unsubscribe: () => void } } }
}

// ─── Test auth client (stores JWT in localStorage) ───────────────────────────

const TEST_SESSION_KEY = 'gcodr_test_session'

/** Seconds remaining before expiry at which we proactively refresh */
const REFRESH_BUFFER_S = 5 * 60 // 5 minutes

function getTokenExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

function isTokenExpired(token: string): boolean {
  const exp = getTokenExp(token)
  if (exp === null) return true
  return exp * 1000 < Date.now() - 60_000
}

/** True when the token is still valid but will expire soon */
function isTokenNearExpiry(token: string): boolean {
  const exp = getTokenExp(token)
  if (exp === null) return false
  const remaining = exp * 1000 - Date.now()
  return remaining > 0 && remaining < REFRESH_BUFFER_S * 1000
}

function getRawStoredSession(): Session | null {
  try {
    const raw = localStorage.getItem(TEST_SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Session
  } catch {
    return null
  }
}

function storeSession(session: Session | null): void {
  if (session) {
    localStorage.setItem(TEST_SESSION_KEY, JSON.stringify(session))
  } else {
    localStorage.removeItem(TEST_SESSION_KEY)
  }
  // Notify listeners within the same tab
  window.dispatchEvent(new CustomEvent('test-auth-change'))
}

// Deduplicate concurrent refresh calls
let refreshPromise: Promise<Session | null> | null = null

async function refreshToken(oldToken: string): Promise<Session | null> {
  try {
    const baseUrl = await getAgentUrl()
    const res = await fetch(`${baseUrl}/api/test-auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: oldToken }),
    })
    if (!res.ok) return null
    const data = await res.json()
    const session: Session = { access_token: data.access_token, user: data.user }
    storeSession(session)
    return session
  } catch {
    return null
  }
}

const testAuthClient: AuthClient = {
  async getSession() {
    const stored = getRawStoredSession()
    if (!stored) return { data: { session: null } }

    // Token still fresh — return it
    if (!isTokenExpired(stored.access_token) && !isTokenNearExpiry(stored.access_token)) {
      return { data: { session: stored } }
    }

    // Token expired or near expiry — try silent refresh
    if (!refreshPromise) {
      refreshPromise = refreshToken(stored.access_token).finally(() => { refreshPromise = null })
    }
    const refreshed = await refreshPromise
    if (refreshed) {
      return { data: { session: refreshed } }
    }

    // Refresh failed — if the old token is still technically valid, use it
    if (!isTokenExpired(stored.access_token)) {
      return { data: { session: stored } }
    }

    // Truly dead — clear and return null (will bounce to login)
    storeSession(null)
    return { data: { session: null } }
  },

  async signInWithPassword({ email, password }) {
    try {
      const baseUrl = await getAgentUrl()
      const res = await fetch(`${baseUrl}/api/test-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const data = await res.json()
        return { error: { message: data.error || 'Login failed' } }
      }
      const data = await res.json()
      storeSession({ access_token: data.access_token, user: data.user })
      return { error: null }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Login failed' } }
    }
  },

  async signUp({ email, password }) {
    try {
      const baseUrl = await getAgentUrl()
      const res = await fetch(`${baseUrl}/api/test-auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const data = await res.json()
        return { error: { message: data.error || 'Signup failed' } }
      }
      const data = await res.json()
      storeSession({ access_token: data.access_token, user: data.user })
      return { error: null }
    } catch (err) {
      return { error: { message: err instanceof Error ? err.message : 'Signup failed' } }
    }
  },

  async signOut() {
    storeSession(null)
  },

  onAuthStateChange(callback: AuthStateCallback) {
    const handler = () => {
      const session = getRawStoredSession()
      callback(session ? 'SIGNED_IN' : 'SIGNED_OUT', session)
    }
    // Listen for same-tab events
    window.addEventListener('test-auth-change', handler)
    // Listen for cross-tab events
    const storageHandler = (e: StorageEvent) => {
      if (e.key === TEST_SESSION_KEY) handler()
    }
    window.addEventListener('storage', storageHandler)
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            window.removeEventListener('test-auth-change', handler)
            window.removeEventListener('storage', storageHandler)
          },
        },
      },
    }
  },
}

// ─── Neon auth client (delegates to existing Neon client) ────────────────────

function createNeonProxy(): AuthClient {
  // Eagerly import neon for sync access (Vite resolves this at build time)
  const neonPromise = import('./neon')

  return {
    async getSession() {
      const { client } = await neonPromise
      return client.auth.getSession()
    },
    async signInWithPassword(params) {
      const { client } = await neonPromise
      return client.auth.signInWithPassword(params)
    },
    async signUp(params) {
      const { client } = await neonPromise
      return client.auth.signUp(params)
    },
    async signOut() {
      const { client } = await neonPromise
      await client.auth.signOut()
    },
    onAuthStateChange(callback) {
      // Return a placeholder subscription; replace once module loads
      let realUnsub: (() => void) | null = null
      neonPromise.then(({ client }) => {
        const { data: { subscription } } = client.auth.onAuthStateChange(callback)
        realUnsub = () => subscription.unsubscribe()
      })
      return {
        data: {
          subscription: {
            unsubscribe: () => { realUnsub?.() },
          },
        },
      }
    },
  }
}

// ─── Export ──────────────────────────────────────────────────────────────────

export const authClient: AuthClient = isTestMode ? testAuthClient : createNeonProxy()
